import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { authApi, bookingApi, customerApi, notificationApi } from './api';
import { SETTINGS_NAV, isSettingsPath } from './settingsNav';
import { useLoader } from './components/common';
import {
  IconHome, IconHistory, IconCashier, IconAttendance, IconCommission,
  IconReport, IconCustomers, IconGlobe, IconSettings, IconPower,
  IconSearch, IconBell, IconChevronDown,
  IconUser, IconLogout, IconStore, IconCheck,
} from './components/icons';
import './App.css';

// Same order as the POS the shop already uses, so nobody has to relearn it.
const NAV = [
  { to: '/', Icon: IconHome, label: 'หน้าหลัก', end: true },
  { to: '/history', Icon: IconHistory, label: 'ประวัติ' },
  { to: '/cashier', Icon: IconCashier, label: 'แคชเชียร์', dot: true },
  { to: '/staff', Icon: IconAttendance, label: 'เข้างาน' },
  { to: '/commission', Icon: IconCommission, label: 'ค่ามือ' },
  { to: '/report', Icon: IconReport, label: 'รายงาน' },
  { to: '/customers', Icon: IconCustomers, label: 'ลูกค้า' },
  { to: '/booking-website', Icon: IconGlobe, label: 'เว็บจอง' },
  { to: '/setting-store', Icon: IconSettings, label: 'ตั้งค่า', settings: true },
];

/** The authenticated shell: rail, header, and the routed page. */
export default function App() {
  const {
    user, branches, branchId, switchBranch, logout,
    switchOrganization, locale, switchLocale,
  } = useAuth();
  const [userOpen, setUserOpen] = useState(false);
  const [orgOpen, setOrgOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [localeOpen, setLocaleOpen] = useState(false);
  const userRef = useRef(null);
  const bellRef = useRef(null);
  const localeRef = useRef(null);
  const organizations = useLoader(() => authApi.organizations(), []);
  const [orgBusy, setOrgBusy] = useState(null);
  const [orgError, setOrgError] = useState('');
  const [onlineCountTick, setOnlineCountTick] = useState(0);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // The live POS is an icon rail on operational pages and expands while a
  // settings route is active so its secondary navigation becomes reachable.
  const onSettings = isSettingsPath(pathname);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  // Header customer search — same endpoint the booking modal uses.
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const searchRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return undefined; }
    const id = setTimeout(() => {
      customerApi.search(q).then((r) => setHits(r.customers || [])).catch(() => setHits([]));
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  // Close whichever dropdown the click landed outside of.
  useEffect(() => {
    const onDown = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setHits([]);
      if (userRef.current && !userRef.current.contains(e.target)) { setUserOpen(false); setOrgOpen(false); }
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
      if (localeRef.current && !localeRef.current.contains(e.target)) setLocaleOpen(false);
    };
    const onEsc = (e) => {
      if (e.key !== 'Escape') return;
      setHits([]);
      setUserOpen(false);
      setOrgOpen(false);
      setBellOpen(false);
      setLocaleOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  // Chromium focuses a native date input when its text area is clicked but can
  // reserve opening the calendar for the small calendar glyph. Every date
  // control in this app should behave as one button, so a click anywhere on the
  // input explicitly opens the native picker. Unsupported browsers simply keep
  // their normal date-input behaviour.
  useEffect(() => {
    const openDatePicker = (event) => {
      const input = event.target instanceof Element
        ? event.target.closest('input[type="date"]')
        : null;
      if (!input || input.disabled || input.readOnly) return;
      if (typeof input.showPicker !== 'function') return;
      try {
        input.showPicker();
      } catch {
        // The browser may reject showPicker when another picker is already open.
      }
    };

    document.addEventListener('click', openDatePicker);
    return () => document.removeEventListener('click', openDatePicker);
  }, []);

  // Route changes dismiss transient header UI instead of carrying an open
  // menu onto the next screen.
  useEffect(() => {
    setHits([]);
    setUserOpen(false);
    setOrgOpen(false);
    setBellOpen(false);
    setLocaleOpen(false);
  }, [pathname]);

  const notifications = useLoader(() => notificationApi.list(), []);
  // Keep the badge in sync with the queue shown on เว็บจอง. The gate is still
  // authoritative for blocking a new walk-in, but its aggregate count can be
  // briefly ahead of the table while an expired/paid request is reconciled.
  const onlineQueue = useLoader(
    () => bookingApi.requests('draft'), [pathname, onlineCountTick]
  );

  // New requests may arrive through the public site in another tab. Refresh
  // quietly, and refresh immediately after this tab confirms or declines one.
  useEffect(() => {
    const refresh = () => setOnlineCountTick((n) => n + 1);
    const interval = setInterval(refresh, 15_000);
    window.addEventListener('saanti:online-requests-changed', refresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener('saanti:online-requests-changed', refresh);
    };
  }, []);

  const chooseLocale = async (next) => {
    const previous = locale;
    setLocaleOpen(false);
    if (next === previous) return;
    try { await switchLocale(next); } catch { /* preference is rolled back by AuthContext */ }
  };

  const chooseOrganization = async (next) => {
    if (next.is_current || orgBusy) return;
    setOrgBusy(next.id);
    setOrgError('');
    try {
      await switchOrganization(next.id);
      setUserOpen(false);
      setOrgOpen(false);
      navigate('/', { replace: true });
      organizations.reload();
      notifications.reload();
    } catch (error) {
      setOrgError(error.message);
    } finally {
      setOrgBusy(null);
    }
  };

  return (
    <div className="app-shell">
      <aside className="rail open">
        <div className="rail-head">
          <div className="rail-logo rail-logo-trio" aria-label="SAANTI, SAANTI Boutique and NIRANA">
            <span className="rail-brand-mark saanti">
              <img
                src="/spa-images/saanti-official-logo.png"
                alt="SAANTI Thai Massage"
              />
            </span>
            <i className="rail-brand-divider" aria-hidden="true" />
            <span className="rail-brand-mark boutique" aria-label="SAANTI Boutique">
              <img src="/spa-images/saanti-boutique-logo.png" alt="" aria-hidden="true" />
            </span>
            <i className="rail-brand-divider" aria-hidden="true" />
            <span className="rail-brand-mark nirana">
              <img
                src="/spa-images/nirana-master-wordmark.png"
                alt="NIRANA Massage and Spa"
              />
            </span>
          </div>
        </div>

        <nav className="rail-nav">
          {NAV.map(({ to, Icon, label, end, dot, settings }) => (
            <div key={to} className="rail-group">
              <NavLink
                to={to}
                end={end}
                title={label}
                className={() =>
                  `rail-item${(settings ? onSettings : pathname === to || (end && pathname === '/')) ? ' active' : ''}`}
              >
                <span className="rail-icon">
                  <Icon />
                  {/* แคชเชียร์ carries a green tick while a drawer round is open. */}
                  {dot && <span className="rail-dot" />}
                  {to === '/booking-website' && onlineQueue.data?.pending > 0 && (
                    <span className="rail-count" aria-label={`${onlineQueue.data.pending} รายการรอจัดสรร`}>
                      {onlineQueue.data.pending > 99 ? '99+' : onlineQueue.data.pending}
                    </span>
                  )}
                </span>
                <span className="rail-label">{label}</span>
              </NavLink>

              {settings && onSettings && (
                <div className="rail-sub">
                  {SETTINGS_NAV.map((s) => (
                    <NavLink
                      key={s.to}
                      to={s.to}
                      className={({ isActive }) => `rail-subitem${isActive ? ' active' : ''}`}
                    >
                      {s.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <button type="button" className="rail-item rail-power" title="ออกจากระบบ" onClick={handleLogout}>
          <span className="rail-icon"><IconPower /></span>
          <span className="rail-label">ออกจากระบบ</span>
        </button>
      </aside>

      <div className="main-col">
        <header className="header">
          {/* Driven by user_branch_role — a user only sees branches they hold. */}
          <div className="branch-select">
            <select value={branchId || ''} onChange={(e) => switchBranch(Number(e.target.value))}>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.displayName || b.code}</option>
              ))}
            </select>
            <IconChevronDown size={16} />
          </div>

          <div className="spacer" />

          <div className="search-wrap" ref={searchRef}>
            <label className="header-search">
              <IconSearch size={17} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="ชื่อ/เบอร์โทรศัพท์"
              />
            </label>
            {hits.length > 0 && (
              <div className="typeahead">
                {hits.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => { setHits([]); setQ(''); navigate(`/customers/${c.id}`); }}
                  >
                    <strong>{c.nickname || c.first_name || 'ลูกค้า'}</strong>
                    <span>#{c.member_code} · {c.phone || '-'}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="bell-wrap" ref={bellRef}>
            <button
              type="button"
              className="icon-btn"
              title="การแจ้งเตือน"
              aria-expanded={bellOpen}
              onClick={() => {
                const opening = !bellOpen;
                setBellOpen(opening);
                setUserOpen(false);
                setOrgOpen(false);
                setLocaleOpen(false);
                if (opening && notifications.data?.unread > 0) {
                  notificationApi.markRead()
                    .then(() => notifications.reload())
                    .catch(() => {});
                }
              }}
            >
              <IconBell size={21} />
              {notifications.data?.unread > 0 && (
                <span className="bell-count">{notifications.data.unread}</span>
              )}
            </button>
            {bellOpen && (
              <div className="bell-panel">
                <div className="bell-head">
                  <strong>แจ้งเตือน</strong>
                  <button
                    className="btn-ghost btn-sm"
                    onClick={async () => {
                      await notificationApi.markRead();
                      notifications.reload();
                    }}
                  >
                    ล้างทั้งหมด
                  </button>
                </div>
                {!notifications.data?.notifications?.length ? (
                  <div className="bell-empty">ไม่มีการแจ้งเตือน</div>
                ) : notifications.data.notifications.map((n) => (
                  // An online request is waiting to be given a หมอนวด, so the
                  // notification carries the screen that does it.
                  <button
                    key={n.id}
                    type="button"
                    className={`bell-row${n.read_at ? '' : ' unread'}${n.payload?.link ? ' link' : ''}`}
                    onClick={async () => {
                      if (!n.read_at) {
                        try {
                          await notificationApi.markRead(n.id);
                          notifications.reload();
                        } catch {
                          return;
                        }
                      }
                      if (n.payload?.link) {
                        setBellOpen(false);
                        navigate(n.payload.link);
                      }
                    }}
                  >
                    <strong>{n.title}</strong>
                    {n.body && <span>{n.body}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="user-wrap" ref={userRef}>
            <button
              type="button"
              className="user-btn"
              aria-expanded={userOpen}
              onClick={() => {
                setUserOpen((o) => !o);
                setOrgOpen(false);
                setBellOpen(false);
                setLocaleOpen(false);
              }}
              title={user?.displayName}
            >
              {/* A plain disc unless there is a photograph — the original shows
                  no initials. */}
              <span className="avatar-box">
                {user?.avatarUrl ? <img src={user.avatarUrl} alt="" /> : null}
              </span>
              <span className="user-name">{user?.displayName}</span>
            </button>

            {userOpen && (
              <div className="user-panel">
                <button type="button" onClick={() => setOrgOpen((v) => !v)}>
                  <IconStore size={17} /> เลือกร้าน
                  <IconChevronDown size={14} className={`grow-left${orgOpen ? ' flip' : ''}`} />
                </button>

                {orgOpen && (
                  <div className="org-list">
                    {(organizations.data?.organizations || []).map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        className={o.is_current ? 'on' : ''}
                        disabled={Boolean(orgBusy)}
                        onClick={() => chooseOrganization(o)}
                      >
                        {orgBusy === o.id ? 'กำลังเปลี่ยน…' : o.name_en}
                        {o.is_current && <IconCheck size={14} />}
                      </button>
                    ))}
                    {orgError && <span className="org-error" role="alert">{orgError}</span>}
                  </div>
                )}

                <button type="button" onClick={() => { setUserOpen(false); navigate('/setting-profile'); }}>
                  <IconUser size={17} /> โปรไฟล์
                </button>
                <button type="button" onClick={handleLogout}>
                  <IconLogout size={17} /> ออกจากระบบ
                </button>
              </div>
            )}
          </div>

          {/* The live control opens a two-item TH/EN menu. Merely opening it
              must not persist a different locale. */}
          <div className="locale-wrap" ref={localeRef}>
            <button
              type="button"
              className="locale-btn"
              onClick={() => {
                setLocaleOpen((o) => !o);
                setUserOpen(false);
                setOrgOpen(false);
                setBellOpen(false);
              }}
              title="เปลี่ยนภาษา"
              aria-expanded={localeOpen}
            >
              <IconGlobe size={19} />
              <span>{locale.toUpperCase()}</span>
              <IconChevronDown size={15} className={localeOpen ? 'flip' : ''} />
            </button>
            {localeOpen && (
              <div className="locale-panel" role="menu">
                {['th', 'en'].map((code) => (
                  <button
                    key={code}
                    type="button"
                    role="menuitemradio"
                    aria-checked={locale === code}
                    className={locale === code ? 'active' : ''}
                    onClick={() => chooseLocale(code)}
                  >
                    {code.toUpperCase()}
                    {locale === code && <IconCheck size={14} />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </header>

        <main className="page">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
